import { v4 as uuidv4 } from 'uuid';

export const generateNodeId = () => `node_${uuidv4()}`;
export const generateConnectionId = () => `connection_${uuidv4()}`;
export const generateWorkflowId = () => `workflow_${uuidv4()}`;

export const createWorkflowNode = (template, position, name = null) => {
  const normalizedTemplate = {
    ...template,
    parameters: template.parameters.map(param => ({
      ...param,
      id: param.id || param.name
    })),
    outputs: template.outputs.map(output => ({
      ...output,
      id: output.id || output.name
    }))
  };

  return {
    id: generateNodeId(),
    templateId: normalizedTemplate.templateId || normalizedTemplate.id,
    name: name || normalizedTemplate.name,
    position,
    parameters: normalizedTemplate.parameters.reduce((acc, param) => {
      acc[param.id] = param.defaultValue || '';
      return acc;
    }, {}),
    template: normalizedTemplate,
  };
};

export const createNodeConnection = (
  sourceNodeId,
  sourceOutputId,
  targetNodeId,
  targetInputId
) => ({
  id: generateConnectionId(),
  sourceNodeId,
  sourceOutputId,
  targetNodeId,
  targetInputId,
});

export const createEmptyWorkflow = (name = 'Nuevo Flujo') => ({
  id: generateWorkflowId(),
  name,
  description: '',
  nodes: [],
  connections: [],
  createdAt: new Date(),
  updatedAt: new Date(),
});

export const validateConnection = (
  sourceNode,
  sourceOutputId,
  targetNode,
  targetInputId,
  existingConnections
) => {
  if (sourceNode.id === targetNode.id) {
    return { valid: false, message: 'No se puede conectar un nodo consigo mismo' };
  }

  const outputExists = sourceNode.template.outputs.some((output) => output.id === sourceOutputId);
  if (!outputExists) {
    return { valid: false, message: 'El output especificado no existe' };
  }

  const existingInputConnections = existingConnections.filter(
    (conn) => conn.targetNodeId === targetNode.id
  );
  if (existingInputConnections.length >= targetNode.template.maxInputs) {
    return { valid: false, message: 'El nodo ya tiene el máximo de conexiones de entrada' };
  }

  const existingOutputConnections = existingConnections.filter(
    (conn) => conn.sourceNodeId === sourceNode.id && conn.sourceOutputId === sourceOutputId
  );
  if (existingOutputConnections.length >= sourceNode.template.maxOutputs) {
    return { valid: false, message: 'El output ya tiene el máximo de conexiones' };
  }

  const connectionExists = existingConnections.some(
    (conn) =>
      conn.sourceNodeId === sourceNode.id &&
      conn.sourceOutputId === sourceOutputId &&
      conn.targetNodeId === targetNode.id &&
      conn.targetInputId === targetInputId
  );
  if (connectionExists) {
    return { valid: false, message: 'Esta conexión ya existe' };
  }

  return { valid: true, message: 'Conexión válida' };
};

export const calculateConnectionPath = (sourcePos, targetPos) => {
  const dx = targetPos.x - sourcePos.x;
  const dy = targetPos.y - targetPos.y;
  const curve = Math.abs(dx) * 0.3;

  return `M ${sourcePos.x} ${sourcePos.y} C ${sourcePos.x + curve} ${sourcePos.y}, ${
    targetPos.x - curve
  } ${targetPos.y}, ${targetPos.x} ${targetPos.y}`;
};

export const getNodeOutputPosition = (node, outputId) => {
  const outputIndex = node.template.outputs.findIndex((output) => output.id === outputId);
  const totalOutputs = node.template.outputs.length;
  const nodeWidth = 200;
  const nodeHeight = 100;

  if (totalOutputs === 1) {
    return {
      x: node.position.x + nodeWidth,
      y: node.position.y + nodeHeight / 2,
    };
  }

  const spacing = nodeHeight / (totalOutputs + 1);
  return {
    x: node.position.x + nodeWidth,
    y: node.position.y + spacing * (outputIndex + 1),
  };
};

export const getNodeInputPosition = (node, inputIndex = 0) => {
  const totalInputs = Math.max(node.template.maxInputs, 1);
  const nodeHeight = 100;

  if (totalInputs === 1) {
    return {
      x: node.position.x,
      y: node.position.y + nodeHeight / 2,
    };
  }

  const spacing = nodeHeight / (totalInputs + 1);
  return {
    x: node.position.x,
    y: node.position.y + spacing * (inputIndex + 1),
  };
};

export const isPointInNode = (point, node) => {
  const nodeWidth = 200;
  const nodeHeight = 100;

  return (
    point.x >= node.position.x &&
    point.x <= node.position.x + nodeWidth &&
    point.y >= node.position.y &&
    point.y <= node.position.y + nodeHeight
  );
};

export const snapToGrid = (position, gridSize = 20) => ({
  x: Math.round(position.x / gridSize) * gridSize,
  y: Math.round(position.y / gridSize) * gridSize,
});

export const exportWorkflow = (workflow) => {
  const dataStr = JSON.stringify(workflow, null, 2);
  const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;

  const exportFileDefaultName = `${workflow.name.replace(/\s+/g, '_')}_${
    new Date().toISOString().split('T')[0]
  }.json`;

  const linkElement = document.createElement('a');
  linkElement.setAttribute('href', dataUri);
  linkElement.setAttribute('download', exportFileDefaultName);
  linkElement.click();
};

export const importWorkflow = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workflow = JSON.parse(e.target.result);
        resolve(workflow);
      } catch (error) {
        reject(new Error('Archivo de workflow inválido'));
      }
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsText(file);
  });

export const formatWorkflowDate = (dateValue) => {
  if (!dateValue) return 'Sin fecha';

  try {
    let date;

    if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'string') {
      const timestamp = parseInt(dateValue, 10);
      if (!isNaN(timestamp) && dateValue === timestamp.toString()) {
        date = new Date(timestamp);
      } else {
        date = new Date(dateValue);
      }
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return 'Sin fecha';
    }

    if (isNaN(date.getTime())) {
      return 'Sin fecha';
    }

    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formateando fecha:', error, dateValue);
    return 'Sin fecha';
  }
};
